import React, { useState } from 'react';
import { useRouter } from 'next/router';
import Logger from '@zola-helpers/server/dist/es/logger';
import {
  getMotifsFromUrl,
  getSilhouettesFromUrl,
  getSeoColorsFromUrl,
  getCardTypeFromUrlParam,
  getExpertAdviceCardsFromCardType,
} from 'cards/components/CardListing/CardListingRedesign/utils';
import {
  CARD_TYPE_MAP,
  PAPER_ADD_ONS_LEAD_CARD_TYPES,
} from '@zola/zola-ui/src/paper/cards/constants/cardTypes';
import { ROUTES_PLP_LEAD_CARD } from 'cards/constants/routesPlpLeadCard';
import { getSEOData } from 'cards/seo';
import { getThumbnailImage } from 'cards/components/CardListing/util';
import { HAS_MOTIFS_BY_CARD_TYPE } from 'cards/constants/hasMotifsByCardType';
import { NAV_DATA } from 'constants/navConstants';
import { fetchUser } from 'api/server/users';
import { fetchFavoriteCardSuiteUUIDs } from 'api/server/favorites';

import Cards from '../../src/cards/pages/Cards';
import { ResponsiveProvider } from '../../src/components/common/ResponsiveContext';
import { initializeStore } from '../../src/store/nextStore';
import { Device } from '../../src/lib/universal/utils/devices';
import { trackPageView } from '../utils/trackPageView';

import {
  requestSSRCardSuites,
  requestSearchCardSuites,
  receiveInitialCardSuites,
  updateMultipleSearchFilters,
  updateCatalogAdminView,
  getCollaborators,
  getMotifs,
} from '../../src/cards/actions/cardCatalogActions';
import { receiveUser } from '../../src/actions/UserActions';
import { receiveFavoriteCardSuiteUUIDs } from '../../src/favorites/redux/actions';
import NextCardListingPage from '../../src/cards/pages/CardListingPage_next';
import { handleGetQueryParamsFromUrl } from '../utils/plpUtils';
import { getExpertAdviceArticles } from '../../src/api/vendorMarketplaceApi';

const CardListingPage = ({
  activeCardEvent,
  isPostAuthSSR,
  isInitialMobile = false,
  isInitialTablet = false,
  isInitialDesktop = false,
  seoFilters,
  seoImage,
  articles = [],
}) => {
  const router = useRouter();
  const { cardType, urlFilter, isDigital } = router.query;
  const [ssrSeoData] = useState(getSEOData(activeCardEvent, seoFilters, !!isDigital));

  const {
    SECONDARY: { PAPER },
  } = NAV_DATA;

  const activeSecondaryLink = PAPER && PAPER[activeCardEvent];

  return (
    <ResponsiveProvider
      isInitialMobile={isInitialMobile}
      isInitialDesktop={isInitialDesktop}
      isInitialTablet={isInitialTablet}
    >
      <Cards>
        <NextCardListingPage
          cardType={cardType}
          urlFilter={urlFilter || ''}
          trackPageView={trackPageView}
          ssrSeoData={ssrSeoData}
          ssrSeoImage={seoImage}
          activeSecondaryLink={activeSecondaryLink}
          activeCardEvent={activeCardEvent}
          isDigital={isDigital}
          isPostAuthSSR={isPostAuthSSR}
          articles={articles}
        />
      </Cards>
    </ResponsiveProvider>
  );
};

export async function getServerSideProps(context) {
  const { cardType, isAdmin, urlFilter, offset = 0, fullUrlQuery, isDigital } = context.query;
  const reduxStore = initializeStore();
  const { dispatch, getState } = reduxStore;
  const activeCardEvent = getCardTypeFromUrlParam(cardType, isDigital);
  const activeFilterStyles = getMotifsFromUrl(urlFilter).map(each => each.toUpperCase());
  const activeFilterColors = getSeoColorsFromUrl(urlFilter).map(each => each.toUpperCase());
  const activeFilterShapes = getSilhouettesFromUrl(urlFilter).map(each => each.toLowerCase());
  const otherFilters = handleGetQueryParamsFromUrl(fullUrlQuery);
  const cardTypeFilter = fullUrlQuery.card_type;
  const cardTypeInfo = ROUTES_PLP_LEAD_CARD[cardTypeFilter || cardType];
  const leadCardType = cardTypeInfo && cardTypeInfo.leadCardType;
  const isPaperAddOns = leadCardType === CARD_TYPE_MAP.paperAddOns;

  const { req } = context;
  const userAgent = req.headers['user-agent'];

  const isInitialMobile = new Device(userAgent).isMobile();
  const isInitialDesktop = new Device(userAgent).isDesktop();

  let props = {};

  try {
    await dispatch(updateCatalogAdminView(isAdmin));

    const cardSuitesFilters = {
      lead_card_type: leadCardType,
      lead_card_types: isPaperAddOns ? PAPER_ADD_ONS_LEAD_CARD_TYPES : null,
      motifs: activeFilterStyles,
      seo_colors: activeFilterColors,
      silhouettes: activeFilterShapes,
      active_only: !isAdmin,
      digital_suite: isDigital || false,
      ...otherFilters,
    };

    await dispatch(updateMultipleSearchFilters(cardSuitesFilters));

    await dispatch(requestSearchCardSuites());

    const userContext = (await fetchUser(req)) || {};
    await dispatch(receiveUser(userContext));

    const cardsPerPage = isInitialMobile ? 24 : 48;
    const [initialSuites] = await Promise.all([
      dispatch(requestSSRCardSuites(offset, cardsPerPage, cardSuitesFilters)),
      dispatch(getCollaborators()),
    ]);

    await dispatch(receiveInitialCardSuites(initialSuites));

    if (HAS_MOTIFS_BY_CARD_TYPE[leadCardType]) {
      await dispatch(getMotifs(leadCardType === CARD_TYPE_MAP.holiday ? leadCardType : 'DEFAULT'));
    }

    const seoFilters = {
      motifs: activeFilterStyles,
      seo_colors: activeFilterColors,
      silhouettes: activeFilterShapes,
      ...otherFilters,
    };

    // Use the thumbnail of the first initial suite as the Twitter / OpenGraph image
    const firstCard = initialSuites?.suites?.[0];
    const seoImage = firstCard ? getThumbnailImage(firstCard.lead_card, cardSuitesFilters) : '';

    const accountId = userContext?.user_role_account_weddings?.[0]?.account_id;
    if (accountId) {
      const favoriteSuiteUUIDs = await fetchFavoriteCardSuiteUUIDs(accountId);
      await dispatch(receiveFavoriteCardSuiteUUIDs(favoriteSuiteUUIDs));
    }

    const initialReduxState = JSON.stringify(getState());
    const isPostAuthSSR = !userContext.is_guest;

    const articles = await getExpertAdviceArticles(
      getExpertAdviceCardsFromCardType(activeCardEvent)
    );

    props = {
      initialReduxState,
      isInitialMobile,
      isInitialDesktop,
      activeCardEvent,
      activeFilterColors,
      isDigital: isDigital || false,
      isPostAuthSSR,
      userContext,
      seoImage,
      seoFilters,
      articles,
    };
  } catch (e) {
    Logger.error('web-wedding PLP error:', e);
  }

  return { props };
}

export default CardListingPage;
